import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { MediaQuery } from "@grandvisionhq/common";
import { Types } from "@grandvisionhq/graphql";
import { Alert } from "@grandvisionhq/iris-ui";
import { useFeatureToggles, useIntl } from "@grandvisionhq/state";
import { storesLocatorResultEvent } from "../../../../data-layer/data-layer/actions.js";
import { useFormattedDistance } from "../../../../hooks/hooks/use-formatted-distance.js";
import BookAppointmentButtonContainer from "../../Buttons/components/StoreFinderItem/Buttons/BookAppointmentButtonContainer.js";
import StoreFinderItem from "./StoreFinderItem.js";
import { useStoreFinderContext } from "../../../../context/StoreFinder/context/StoreFinder/context.js";
import { areServicesAvailable } from "../../../../utils/utils/store-utils.js";
const StoreFinderItemContainer = ({
  addressSize,
  className,
  store,
  loading,
  onMouseEnter,
  onMouseLeave,
  view = "list",
  buttons,
  variant,
  isPreferredStore,
  showAttributesAsTags
}) => {
  const { isAABEnabled } = useFeatureToggles();
  const { config, stores, getStoreDetailsUrl, urls } = useStoreFinderContext();
  const { hideEmail, hidePhone } = config;
  const { hasLabel, getLabel, getFormattedLabel } = useIntl();
  const isPhone = MediaQuery.useIsPhone();
  const labels = {
    storeDetailsLink: getLabel("stores.component.storeFinderItem.action.storeDetails"),
    alert: getFormattedLabel("appointments.component.appointmentTypes.noAppointmentsAlert", {
      hideLabelKey: true,
      parseLink: true
    })
  };
  const storeDetailsUrl = urls?.storeDetails && getStoreDetailsUrl(store);
  const showBookAppointmentCTA = areServicesAvailable(store, [
    Types.ServicesNotAvailable.APPOINTMENTS
  ]);
  const onMouseEnterHandler = React.useCallback(
    () => onMouseEnter && onMouseEnter(store),
    [store, onMouseEnter]
  );
  const onMouseLeaveHandler = React.useCallback(
    () => onMouseLeave && onMouseLeave(store),
    [store, onMouseLeave]
  );
  const onStoresLocatorResultEvent = React.useCallback(
    ({
      store: store2,
      interaction,
      preferredStore,
      searchPosition,
      searchResults
    }) => {
      const dataLayerEvent = storesLocatorResultEvent({
        interaction,
        store: store2,
        preferredStore,
        searchPosition,
        searchResults
      });
      DataLayer.pushEvent(dataLayerEvent);
    },
    []
  );
  const onExpandOpeningHoursCallback = React.useCallback(() => {
    onStoresLocatorResultEvent({
      store,
      preferredStore: isPreferredStore,
      interaction: "opening times click",
      searchPosition: stores.indexOf(store) + 1
    });
  }, [onStoresLocatorResultEvent, isPreferredStore, store, stores]);
  const onStoreDetailLinkClickCallback = React.useCallback(() => {
    onStoresLocatorResultEvent(
      view === "list" ? {
        store,
        preferredStore: isPreferredStore,
        interaction: "store details click",
        searchPosition: stores.indexOf(store) + 1
      } : {
        store,
        preferredStore: isPreferredStore,
        interaction: "map click - store details",
        searchResults: stores.length
      }
    );
  }, [onStoresLocatorResultEvent, store, view, stores, isPreferredStore]);
  const onTitleLinkClickCallback = React.useCallback(() => {
    onStoresLocatorResultEvent(
      view === "list" ? {
        store,
        preferredStore: isPreferredStore,
        interaction: "store details click",
        searchPosition: stores.indexOf(store) + 1
      } : {
        store,
        preferredStore: isPreferredStore,
        interaction: "map click - store details",
        searchResults: stores.length
      }
    );
  }, [onStoresLocatorResultEvent, store, view, stores, isPreferredStore]);
  const distance = useFormattedDistance(store?.distance);
  const bookAppointmentCTA = isAABEnabled && store.appointmentsAvailable || !isAABEnabled ? /* @__PURE__ */ React.createElement(
    BookAppointmentButtonContainer,
    {
      view,
      store,
      isPreferredStore
    }
  ) : hasLabel(labels.alert) ? /* @__PURE__ */ React.createElement(
    Alert,
    {
      variant: "info",
      title: labels.alert,
      className: "alert-m-default aab-drop-in-widget__alert"
    }
  ) : null;
  return /* @__PURE__ */ React.createElement(
    StoreFinderItem,
    {
      className,
      store: view === "map" ? { ...store, storeDetailImage: void 0 } : store,
      addressSize,
      showEmailAddress: !hideEmail,
      showPhone: !hidePhone,
      showAttributesAs: showAttributesAsTags ? "tags" : "icons",
      buttons: buttons ? buttons : showBookAppointmentCTA ? bookAppointmentCTA : null,
      loading,
      labels,
      storeDetailsUrl,
      onExpandOpeningHoursCallback,
      onClickStoreDetailsLinkCallback: onStoreDetailLinkClickCallback,
      onClickStoreDetailsTitleCallback: onTitleLinkClickCallback,
      onMouseEnter: onMouseEnterHandler,
      onMouseLeave: onMouseLeaveHandler,
      view,
      variant: variant || (view === "list" && !isPhone ? "ghosted" : void 0),
      distance
    }
  );
};
export {
  StoreFinderItemContainer as default
};
//# sourceMappingURL=StoreFinderItemContainer.js.map
